import React, { Suspense, useEffect } from 'react'
import { Switch, Route, AuthRoute, NotFound, useRouteMatch, useLocation } from '@fs/zion-router'
import ErrorBoundary from '@fs/zion-error-boundary'
import { ApolloClient, InMemoryCache, ApolloProvider, createHttpLink } from '@apollo/client'
import { setContext } from '@apollo/client/link/context'
import { Global, css } from '@emotion/core'
import { Separator, useOverlay } from '@fs/zion-ui'
import { Title } from '@fs/zion-head'
import { i18n } from '@fs/zion-locale'
import { useTranslation } from 'react-i18next'
import { Redirect, useHistory } from 'react-router-dom'
import { createPersistedQueryLink } from 'apollo-link-persisted-queries'
import { baseUrl } from '@fs/zion-config'
import { EscHandlerProvider } from '@fs/zion-ui/dist/es/use-esc-handler'
import { PromptSignInAuthRoute } from './router'
import PreviewRenderer from './components/home/PreviewRenderer'
import LargeLoadingSpinner from './components/LargeLoadingSpinner'
import openSans from './components/type/open-sans'
import NavBar from './components/NavBar'
import Chat from './components/chat/Chat'
import { PlaylistProvider } from './components/PlaylistContext'
import { ChatProvider } from './state/ChatState'
import { ChatOverlayProvider } from './components/chat/ChatOverlayProvider'
import { SpeakerProvider } from './components/SpeakerProvider'
import { SessionProvider } from './components/SessionProvider'
import { BookmarksProvider } from './components/chat/BookmarksProvider'
import ScrollToTop from './components/ScrollToTop'
import sessionDetailRoutes from './helpers/sessionDetailRoutMatches'
import TempREGISTERME from './components/TempREGISTERME'
import { VendorProvider } from './components/VendorProvider'
import { SupportPageProvider } from './components/volunteer/VolunteerSupportContext'
import LeavingLinkInterceptor from './global/LeavingLinkInterceptor'
import PageTitle from './components/PageTitle'
import { Chat as ChatSettings } from './global/chatSettingsConfig'

const HomePage = React.lazy(() => import('./components/home/HomePage'))
const SpeakersPage = React.lazy(() => import('./components/speakers/SpeakersPage'))
const ExpoHallPage = React.lazy(() => import('./components/expohall/ExpoHallPage'))
const VolunteerPage = React.lazy(() => import('./components/volunteer/VolunteerPage'))
const VolunteerSignupPage = React.lazy(() => import('./components/volunteer/VolunteerSignup'))
const VolunteerSupportPage = React.lazy(() => import('./components/volunteer/SupportPage'))
const VirtualBoothPage = React.lazy(() => import('./components/virtual-booth/VirtualBoothPage'))
const PlaylistPage = React.lazy(() => import('./components/myplaylist/MyPlaylistPage'))
const SessionDetailPage = React.lazy(() => import('./components/sessions/SessionDetailPage'))
const SearchPage = React.lazy(() => import('./components/search/SearchPage'))
const SearchByTypePage = React.lazy(() => import('./components/search/SearchByTypePage'))
const SearchResultsPage = React.lazy(() => import('./components/search/SearchResultsPage'))
const SpeakerDetailPage = React.lazy(() => import('./components/speakers/SpeakerDetailPage'))
const SeriesDetailPage = React.lazy(() => import('./components/series/SeriesDetailPage'))
const SeriesPage = React.lazy(() => import('./components/series/SeriesPage'))
const ChatRoomInvite = React.lazy(() => import('./components/chat/ChatRoomInvite'))
const AccountLinkingPage = React.lazy(() => import('./components/AccountLinkingPage'))
const VendorLinkingPage = React.lazy(() => import('./components/VendorLinkingPage'))
const FAQPage = React.lazy(() => import('./components/frequently-asked-questions/FAQPage'))
const GlobalLoggedOutDialog = React.lazy(() => import('./global/GlobalLoggedOutDialog'))

const AskUsAnythingRedirect = () => {
  return ChatSettings.Settings.ASK_EXPERT_ENABLED ? <Redirect to="/?aua=true" /> : <Redirect to="/" />
}
const overrideLayoutBandCss = css`
  width: 100%;
  z-index: 0;
  & *[max-width='xxl'] {
    max-width: min(100vw, 1440px) !important;
    padding: 0 !important;
  }
`

function App() {
  const [t] = useTranslation()
  const { search } = useLocation()
  const overlay = useOverlay()
  const params = new URLSearchParams(search)
  const previewId = params.get('previewId')
  const registrationRouteMatch = useRouteMatch('/finish-registration')

  const siteSettings = {
    devSite: {
      apiKey: '1kKzZg3LRTYRsG00daTThwBcIcxb14SxlRQWpun',
      uri: 'https://qa3.familysearch.psdops.com/graphql/delivery/rootstech',
    },
    prodSite: {
      apiKey: 'KRcgFxcxtKMkiohRf7ZWPuFlYvL8hDevkImXRUa',
      uri: 'https://rootstech-data.prod.familysearch.psdops.com/graphql/delivery/rootstech',
    },
  }

  const isProd = !!(baseUrl.includes('www') || previewId)

  const selectedSite = isProd ? siteSettings.prodSite : siteSettings.devSite
  const link = createHttpLink({
    uri: selectedSite.uri,
  })

  const authLink = setContext((_, { headers }) => {
    return {
      headers: {
        ...headers,
        'x-api-key': selectedSite.apiKey,
        'Accept-Language': i18n.language === 'zh-hans' ? 'zh-CN' : i18n.language,
      },
    }
  })

  const persistedQueryLink = createPersistedQueryLink({ useGETForHashedQueries: true }).concat(authLink.concat(link))

  const client = new ApolloClient({
    link: persistedQueryLink,
    cache: new InMemoryCache(),
  })
  const appHistory = useHistory()

  // Root comes with a "Skip to main content link"
  // we don't have access to it, but we can move the link around
  useEffect(() => {
    const skipToContentLink = document.querySelector('[href="#main"]')
    // both of these if-checks should always be true, just being safe
    if (skipToContentLink) {
      skipToContentLink.href = '#main-content'

      const oldMainContent = document.querySelector('#main')
      const newMainContent = document.querySelector('#main-content')
      if (newMainContent && oldMainContent) {
        newMainContent.setAttribute('aria-label', oldMainContent.getAttribute('aria-label'))
        oldMainContent.setAttribute('aria-label', '')
      }
    }
  }, [])

  return (
    <>
      <Title>{t('site-title', 'RootsTech 2021')}</Title>
      <PageTitle />
      <Global styles={openSans} />
      <ErrorBoundary>
        <ApolloProvider client={client}>
          <PlaylistProvider>
            <SpeakerProvider>
              <SessionProvider>
                <VendorProvider>
                  <ChatProvider>
                    <ChatOverlayProvider appHistory={appHistory}>
                      <BookmarksProvider>
                        <SupportPageProvider>
                          <Suspense fallback={<span />}>
                            <div style={{ display: 'flex', justifyContent: 'center' }}>
                              <div css={overrideLayoutBandCss}>
                                <NavBar />
                                <main id="main-content">
                                  <Suspense fallback={<LargeLoadingSpinner />}>
                                    <ScrollToTop>
                                      {previewId ? (
                                        <PreviewRenderer previewId={previewId} />
                                      ) : (
                                        <Switch>
                                          <Route
                                            exact
                                            path="/(home|homepre|homeduring|homepost|oldstagepre|oldstageduring|oldstagepost)"
                                            component={HomePage}
                                          />
                                          <Route exact path="/series" component={SeriesPage} />
                                          <Route path="/series/:id" component={SeriesDetailPage} />
                                          <Redirect path="/user" to="/" />
                                          <Redirect from="/calendar" to="/" />
                                          <Route exact path="/speakers" component={SpeakersPage} />
                                          <Route path="/speakers/:id/:lan" component={SpeakerDetailPage} />
                                          <Route exact path="/search" component={SearchPage} />
                                          <Route path="/search/:type(language|region)" component={SearchByTypePage} />
                                          <Route path="/search/results" component={SearchResultsPage} />
                                          <PromptSignInAuthRoute
                                            path="/registered-session"
                                            component={SessionDetailPage}
                                            overlay={overlay}
                                          />
                                          <Route path={sessionDetailRoutes} component={SessionDetailPage} />
                                          <Route exact path="/expohall" component={ExpoHallPage} />
                                          <Route path="/expohall/:id" component={VirtualBoothPage} />
                                          <Route path="/account-linking/:id" component={AccountLinkingPage} />
                                          <Route path="/vendor-linking/:id" component={VendorLinkingPage} />
                                          <Route path="/faq" component={FAQPage} />
                                          <AuthRoute
                                            path="/chat/invite/:chatRoom"
                                            component={
                                              ChatSettings.Settings.SITE_CHAT_ENABLED ? ChatRoomInvite : HomePage
                                            }
                                          />
                                          <AuthRoute exact path="/volunteer" component={VolunteerPage} />
                                          <AuthRoute path="/volunteer/signup" component={VolunteerSignupPage} />
                                          <AuthRoute path="/volunteer/support" component={VolunteerSupportPage} />
                                          <PromptSignInAuthRoute
                                            path="/playlist"
                                            component={PlaylistPage}
                                            overlay={overlay}
                                          />
                                          <AuthRoute path="/tempregister" component={TempREGISTERME} />
                                          <Route exact path="(/|/?)" component={HomePage} />
                                          <Route exact path="/chat/ask-us-anything" component={AskUsAnythingRedirect} />
                                          <Route component={NotFound} />
                                        </Switch>
                                      )}
                                    </ScrollToTop>
                                  </Suspense>

                                  <EscHandlerProvider>
                                    <GlobalLoggedOutDialog {...overlay} />
                                  </EscHandlerProvider>
                                </main>
                              </div>
                            </div>

                            {!registrationRouteMatch && <Chat />}

                            <LeavingLinkInterceptor />

                            <Separator size="xxl" />
                          </Suspense>
                        </SupportPageProvider>
                      </BookmarksProvider>
                    </ChatOverlayProvider>
                  </ChatProvider>
                </VendorProvider>
              </SessionProvider>
            </SpeakerProvider>
          </PlaylistProvider>
        </ApolloProvider>
      </ErrorBoundary>
    </>
  )
}
export default App
